/**
 * KAPE — Course Builder UI. Route: /kcis/admin/university/course-builder
 *
 * Wizard-style editor with 5 stages: Intake → Blueprint → Modules & Lessons →
 * Assessments → Slides & Narration → Review & Publish.
 *
 * All generation is streamed through the KAPE backend at /api/kcis/admin/kape.
 * Nothing writes into the LMS until "Publish" is invoked and the readiness
 * checklist is satisfied.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import KcisAdminShell, { KCIS_ADMIN_PALETTE as PAL } from "./KcisAdminShell";

// ────────────────────────────────────────────────────────────────
// List view — /kcis/admin/university/course-builder
// ────────────────────────────────────────────────────────────────
export default function KapeCourseBuilder() {
  const [items, setItems] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [creating, setCreating] = useState(false);
  const [aiBuilderEnabled, setAiBuilderEnabled] = useState(false);
  const navigate = useNavigate();

  // Feature-flag lookup — only reveal the AI Builder CTA when
  // KAPE_AI_BUILDER_ENABLED=true. Backend enforces this too.
  useEffect(() => {
    let alive = true;
    api.get("/kcis/admin/kape/ai-builder/config", { timeout: 10000 })
      .then(({ data }) => { if (alive) setAiBuilderEnabled(!!data?.enabled); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const reload = useCallback(async () => {
    setLoadError("");
    setItems(null);
    try {
      const { data } = await api.get("/kcis/admin/kape/courses", { timeout: 20000 });
      setItems(data.items || []);
    } catch (err) {
      const status = err?.response?.status;
      const code = status ? `HTTP ${status}` : (err?.code || "network");
      // 401 is handled globally by the admin interceptor; anything else
      // becomes an actionable in-page error with a retry button.
      if (status !== 401) {
        setLoadError(`Unable to load courses (${code}). Please retry, or contact support with reference KAPE-LIST-${Date.now()}.`);
        setItems([]);
      }
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const startTestCourse = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/kcis/admin/kape/courses", {
        intake: {
          title: "Introduction to Microscopic Hair & Scalp Analysis",
          course_code: "TEST-101",
          subtitle: "Foundational microscopy for scalp clinicians",
          description: "A short introductory course covering microscopic evaluation of the hair shaft and scalp for entry-level trichology students.",
          category: "Continuing Education",
          program: "Course: Introduction to Microscopic Hair & Scalp Analysis",
          credential_level: "Introductory",
          audience: "Practicing cosmetologists and entry-level trichology students.",
          prerequisites: "None",
          delivery_format: "self-paced online",
          status_flag: "internal_test",
          total_clock_hours: 3,
          ce_hours: 3,
          module_count: 1,
          lesson_count: 3,
          course_length_weeks: 1,
          learning_objectives: [
            "Describe the anatomy of a hair shaft under 40x magnification.",
            "Identify common scalp conditions on a microscopic slide.",
            "Document clinical microscopy findings using the KCIS intake template.",
          ],
          required_competencies: ["Microscope operation", "Scalp assessment vocabulary"],
          completion_requirements: "Complete all 3 lessons and pass the module quiz (>=80%).",
          passing_score: 80,
          grading_method: "auto (quiz)",
          teaching_philosophy: "Warm, clinical, evidence-informed — the KCIS voice.",
          tone: "professional, warm, pedagogically rigorous",
          reading_level: "college / practitioner",
          depth: "introductory — foundational vocabulary and hands-on technique",
          required_terminology: ["cuticle", "cortex", "medulla", "follicle", "anagen", "telogen"],
          must_include_topics: ["microscope calibration", "slide preparation", "safe scalp handling"],
          must_exclude_topics: ["surgical procedures"],
          practical_activities: "Prepare 3 slides from provided sample kit and photograph findings.",
          clinical_activities: "",
          reflection_activities: "Journal one sentence per lesson describing what surprised you.",
          references: [],
          generate_syllabus: true,
          generate_outline: true,
          generate_lessons: true,
          generate_workbook: true,
          generate_assignments: true,
          generate_quizzes: true,
          generate_final_exam: false,
          generate_answer_keys: true,
          generate_rubrics: true,
          generate_marketing_copy: true,
          generate_certificate_language: true,
          generate_slides: true,
          generate_narration: true,
        },
      });
      toast.success("TEST-101 draft created");
      navigate(`/kcis/admin/university/course-builder/${data.course.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create course");
    } finally {
      setCreating(false);
    }
  };

  const createBlank = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/kcis/admin/kape/courses", {
        intake: { title: "New KAPE Course", course_code: "", module_count: 3, lesson_count: 3, total_clock_hours: 20 },
      });
      navigate(`/kcis/admin/university/course-builder/${data.course.id}`);
    } catch (e) {
      toast.error("Failed to create draft");
    } finally { setCreating(false); }
  };

  return (
    <KcisAdminShell>
      <div className="max-w-6xl mx-auto px-8 py-10" data-testid="kape-list">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest" style={{ color: PAL.muted }}>KAPE</div>
            <h1 className="text-3xl font-semibold mt-1">Course Builder</h1>
            <p className="mt-2 text-sm max-w-2xl" style={{ color: PAL.muted }}>
              Author, generate, review, and publish complete KCIS university courses.
              Drafts here are administrator-only until published to the LMS.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap justify-end">
            {aiBuilderEnabled && (
              <Link
                to="/kcis/admin/kape/ai-builder"
                className="rounded-full px-4 py-2 text-sm font-semibold shadow-sm inline-flex items-center gap-1.5"
                style={{ background: "#B084E8", color: "#0F0B14", boxShadow: "0 0 0 2px rgba(176,132,232,0.35)" }}
                data-testid="kape-btn-create-learning-experience"
                title="Guided AI Builder — enter 6 inputs, review a blueprint, generate a full editable draft."
              >
                ✨ Create Learning Experience
              </Link>
            )}
            <button onClick={startTestCourse} disabled={creating}
                    className="rounded-full px-4 py-2 text-sm font-medium"
                    style={{ background: PAL.accent, color: "#0F0B14" }}
                    data-testid="kape-btn-create-test-101">
              {creating ? "Creating…" : "Create TEST-101 draft"}
            </button>
            <button onClick={createBlank} disabled={creating}
                    className="rounded-full px-4 py-2 text-sm font-medium border"
                    style={{ borderColor: PAL.border, color: PAL.text }}
                    data-testid="kape-btn-create-blank">
              New blank draft
            </button>
          </div>
        </div>

        <div className="mt-8">
          {items === null && !loadError && <div style={{ color: PAL.muted }} data-testid="kape-list-loading">Loading course drafts…</div>}
          {loadError && (
            <div className="rounded-2xl p-6 border" style={{ borderColor: "#7A1F1F", background: "#241014" }} data-testid="kape-list-error">
              <div className="text-sm font-semibold" style={{ color: "#FDA4AF" }}>Could not load courses</div>
              <div className="mt-1 text-xs" style={{ color: PAL.muted }}>{loadError}</div>
              <button onClick={reload}
                      className="mt-4 rounded-full px-4 py-1.5 text-xs font-medium"
                      style={{ background: PAL.accent, color: "#0F0B14" }}
                      data-testid="kape-list-retry">
                Retry
              </button>
            </div>
          )}
          {!loadError && items?.length === 0 && (
            <div className="rounded-2xl p-8 text-center border"
                 style={{ borderColor: PAL.border, background: PAL.panel }}
                 data-testid="kape-empty">
              <div className="text-lg font-semibold">No course drafts yet</div>
              <div className="mt-2 text-sm" style={{ color: PAL.muted }}>
                Start with the TEST-101 draft to validate the end-to-end pipeline, or create a blank draft.
              </div>
            </div>
          )}
          {items?.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {items.map((c) => (
                <Link to={`/kcis/admin/university/course-builder/${c.id}`} key={c.id}
                      className="block rounded-xl p-5 border transition-transform hover:-translate-y-0.5"
                      style={{ borderColor: PAL.border, background: PAL.panel }}
                      data-testid={`kape-course-${c.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-semibold">{c.title}</div>
                    <span className="text-xs rounded-full px-2 py-0.5"
                          style={{ background: statusColor(c.status), color: "white" }}>{c.status}</span>
                  </div>
                  <div className="mt-1 text-xs" style={{ color: PAL.muted }}>{c.course_code || "no code yet"} · {c.status_flag}</div>
                  <div className="mt-3 flex items-center gap-4 text-xs" style={{ color: PAL.muted }}>
                    <span>{c.module_count} modules</span>
                    <span>{c.lesson_count} lessons</span>
                    <span>{c.quiz_count} quizzes</span>
                    <span>{c.readiness_percent}% ready</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </KcisAdminShell>
  );
}

function statusColor(status) {
  switch (status) {
    case "published": return "#059669";
    case "ready_to_publish": return "#0891B2";
    case "review": return "#7C3AED";
    case "assessments_ready": return "#8B5CF6";
    case "modules_ready": return "#6366F1";
    case "blueprint_ready": return "#4F46E5";
    default: return "#6B7280";
  }
}
